<template>
  <div
    class="overflow-hidden transition-all duration-300 ease-out"
    :class="{
      'opacity-0': loading || institutions.length <= 1,
      'opacity-100': !loading && institutions.length > 1,
    }"
    :style="{
      height: !loading && institutions.length > 1 ? `${containerHeight}px` : '0px',
    }"
  >
    <div v-if="institutions.length > 1" class="border-b border-blue-400/40" ref="contentRef">
      <div class="relative" ref="triggerRef">
        <button
          @click="toggleDropdown"
          tabindex="0"
          aria-haspopup="dialog"
          :aria-expanded="isOpen"
          class="group pl-5 pr-3.5 py-2 text-white w-full flex items-center justify-between text-sm bg-gradient-to-t transition"
          :class="{
            'from-blue-800/70 via-blue-900/60 to-blue-900/50': isOpen,
            'hover:text-blue-100 focus-visible:text-blue-100 from-blue-900/60 via-blue-900/20 to-transparent':
              !isOpen,
          }"
        >
          <span class="truncate">
            <span class="sr-only">{{ t('labels.institution') }}</span>
            <template v-if="selectedInstitution">
              <strong class="font-bold">{{ selectedInstitution.abbreviation }}</strong>
              <span class="opacity-70 mx-1">-</span>
              <span class="font-normal opacity-80">{{ selectedInstitution.name }}</span>
            </template>
            <template v-else>
              {{ t('selectInstitution') }}
            </template>
          </span>
          <s-icon
            name="chevron-down"
            size="18"
            class="opacity-70 transition group-hover:opacity-100"
            :class="{
              'rotate-180': isOpen,
            }"
          />
        </button>

        <!-- Dropdown -->
        <Teleport to="body">
          <TransitionRoot
            :show="isOpen"
            leave="transition ease-in duration-100"
            leave-from="opacity-100"
            leave-to="opacity-0"
            @after-leave="isOpen = false"
          >
            <div
              ref="dropdownRef"
              role="dialog"
              :aria-label="t('labels.switchInstitution')"
              :style="floatingStyles"
              @mousedown.stop
              class="z-50 rounded-[calc(0.5rem-1px)] shadow-card bg-white"
            >
              <s-select-filter-content
                ref="filterContentRef"
                :options="institutionOptions"
                v-model="selectedInstitutionId"
                @close="closeDropdown"
              />
            </div>
          </TransitionRoot>
        </Teleport>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, Teleport, watch, type ComponentPublicInstance, toRef} from 'vue';
import {Institution} from '../../types/entities/institution.js';
import {router, usePage} from '@inertiajs/vue3';
import {useI18n} from 'vue-i18n';
import {TransitionRoot} from '@headlessui/vue';
import {autoUpdate, flip, offset, shift, useFloating} from '@floating-ui/vue';
import SSelectFilterContent from '../../design-system/SSelectFilterContent.vue';
import SIcon from '../../design-system/SIcon.vue';
import {useEventListener} from '@vueuse/core';
import {useAxios} from '@vueuse/integrations/useAxios';

const page = usePage();

const containerHeight = ref(0);
const contentRef = ref<HTMLElement | null>(null);

const {data, isLoading} = useAxios<Institution[]>(route('api.v1.institutions.index'));
const loading = computed(() => isLoading?.value ?? false);
const institutions = computed<Institution[]>(() => data?.value || []);

watch(data, () => {
  updateSelectedInstitution();

  nextTick(() => {
    measureContentHeight();
  });
});

// Watch changes to the active institution being pushed by the server.
watch(
  () => page.props.activeInstitution,
  (newValue) => {
    if (newValue?.id !== selectedInstitutionId?.value) {
      updateSelectedInstitution();
    }
  }
);

const institutionOptions = computed<{label: string; value: string | number}[]>(() =>
  institutions.value.map((institution) => ({
    label: `${institution.abbreviation} - ${institution.name}`,
    value: institution.id,
  }))
);
const isOpen = ref(false);
const selectedInstitutionId = ref<string | number | null>(page.props.activeInstitution?.id || null);

// Floating UI refs and setup
const triggerRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const filterContentRef = ref<ComponentPublicInstance<typeof SSelectFilterContent> | null>(null);
const middleware = ref([
  offset(8),
  shift(),
  flip(),
  {
    name: 'leftOffset',
    fn: ({x}) => ({
      x: Math.max(x, 8),
    }),
  },
]);
const {floatingStyles} = useFloating(triggerRef, dropdownRef, {
  placement: 'bottom-start',
  middleware,
  whileElementsMounted: autoUpdate,
});

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const selectedInstitution = computed(() => {
  return institutions.value.find((inst) => inst.id === selectedInstitutionId.value) || null;
});

watch(selectedInstitutionId, (newInstitutionId, previousInstitutionId) => {
  if (newInstitutionId === previousInstitutionId) {
    return;
  }

  const isUserInitiated = newInstitutionId !== page.props.activeInstitution?.id;
  if (!isUserInitiated) {
    return;
  }

  const institution = institutions.value.find((inst) => inst.id === newInstitutionId);
  if (institution) {
    handleInstitutionChange(institution);
  }
});

const updateSelectedInstitution = () => {
  if (page.props.activeInstitution) {
    selectedInstitutionId.value = page.props.activeInstitution.id;
  } else if (institutions.value.length > 0) {
    selectedInstitutionId.value = institutions.value[0].id;
  }
};

const measureContentHeight = () => {
  if (contentRef.value && institutions.value.length > 1) {
    containerHeight.value = contentRef.value.offsetHeight;
  }
};

const handleInstitutionChange = (institution: Institution) => {
  router.visit(
    route('institutions.courses.index', {
      institution: institution.id,
    })
  );
};

const toggleDropdown = async () => {
  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    await nextTick();
    setTimeout(() => {
      if (filterContentRef.value) {
        // Auto-focus the search input when opening the dropdown
        const searchInput = filterContentRef.value.$el?.querySelector('input');
        if (searchInput) {
          (searchInput as HTMLInputElement).focus();
        }
      }
    }, 50);
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  nextTick(() => {
    if (triggerRef.value) {
      // Return focus to the trigger button when dropdown closes
      const triggerButton = triggerRef.value.querySelector('button');
      if (triggerButton) {
        (triggerButton as HTMLElement).focus();
      }
    }
  });
};

// Close dropdown when clicking outside
const handleClickOutside = (event: Event) => {
  if (isOpen.value && triggerRef.value && !triggerRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

// Close dropdown when Escape key is pressed
const handleKeydown = (event: KeyboardEvent) => {
  if (isOpen.value && event.key === 'Escape') {
    closeDropdown();
  }
};

useEventListener(document, 'click', handleClickOutside);
useEventListener(document, 'keydown', handleKeydown);
</script>

<i18n>
{
  "en": {
    "selectInstitution": "Select institution...",
    "labels.institution": "Institution:",
    "labels.switchInstitution": "Switch institution"
  },
  "fr": {
    "selectInstitution": "Sélectionner une institution...",
    "labels.institution": "Institution :",
    "labels.switchInstitution": "Changer d'institution"
  }
}
</i18n>
